import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { useUserAuth } from '../context/UserAuthContext';
import { toast } from 'sonner';
import { Loader2, User, Building2, ArrowLeft, Save, Mail, Calendar } from 'lucide-react';

const UserSettings = () => {
  const { user, updateProfile } = useUserAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    company: user?.company || '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await updateProfile(formData);
    
    if (result.success) {
      toast.success('Profile updated successfully!');
      navigate('/user/dashboard');
    } else {
      toast.error(result.error || 'Update failed');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F6F7FB] pt-32 pb-20">
      <Helmet>
        <title>Settings | Liquicent</title>
      </Helmet>
      
      <div className="max-w-2xl mx-auto px-6">
        <div className="mb-8">
          <Button
            onClick={() => navigate('/user/dashboard')}
            variant="ghost"
            className="mb-4 text-[#64748B] hover:text-[#1A3A8F] hover:bg-transparent"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-[#0F172A] mb-2">Profile Settings</h1>
          <p className="text-[#64748B]">Update your account information</p>
        </div>

        <Card 
          className="p-8 bg-white border border-[#E5E7EB] rounded-xl"
          style={{ boxShadow: '0px 6px 16px rgba(0,0,0,0.06)' }}
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-[#0F172A] text-sm font-medium">
                Full Name
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#94A3B8] w-5 h-5" />
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                  className="pl-10 bg-[#F6F7FB] border-[#E5E7EB] text-[#0F172A] focus:border-[#1A3A8F]"
                  placeholder="John Doe"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company" className="text-[#0F172A] text-sm font-medium">
                Company
              </Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#94A3B8] w-5 h-5" />
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                  className="pl-10 bg-[#F6F7FB] border-[#E5E7EB] text-[#0F172A] focus:border-[#1A3A8F]"
                  placeholder="Acme Inc"
                />
              </div>
            </div>

            <div className="pt-6 border-t border-[#E5E7EB]">
              <div className="flex gap-4">
                <Button
                  type="button"
                  onClick={() => navigate('/user/dashboard')}
                  variant="outline"
                  className="flex-1 border-[#E5E7EB] text-[#64748B] hover:bg-[#F6F7FB]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#1A3A8F] hover:bg-[#142C6D] text-white"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>

          <div className="mt-8 pt-8 border-t border-[#E5E7EB]">
            <h3 className="text-lg font-semibold text-[#0F172A] mb-4">Account Information</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-[#F6F7FB] rounded-lg">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#64748B]" />
                  <span className="text-sm text-[#64748B]">Email</span>
                </div>
                <span className="text-sm text-[#0F172A] font-medium">{user?.email}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-[#F6F7FB] rounded-lg">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-[#64748B]" />
                  <span className="text-sm text-[#64748B]">Member Since</span>
                </div>
                <span className="text-sm text-[#0F172A] font-medium">
                  {new Date(user?.created_at).toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default UserSettings;
